import * as Clipboard from 'expo-clipboard';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { SquadViewModal } from '@/components/fantasy/SquadViewModal';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { ShareButton } from '@/components/ui/ShareButton';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useLeaveMiniLeague,
  useMiniLeagueLeaderboard,
  useMyMiniLeagues,
} from '@/features/fantasy/hooks';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { LeaderboardEntry } from '@/lib/supabase/types';

export default function MiniLeagueScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const [copied, setCopied] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<LeaderboardEntry | null>(null);

  const boardQuery = useMiniLeagueLeaderboard(id);
  const leaguesQuery = useMyMiniLeagues();
  const leaveMutation = useLeaveMiniLeague();
  const league = (leaguesQuery.data ?? []).find(
    (membership) => membership.mini_league?.id === id,
  )?.mini_league;

  const copyCode = () => {
    if (!league) return;
    Clipboard.setStringAsync(league.invite_code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard unavailable - code remains visible.
      });
  };

  const onLeave = () => {
    if (!confirmLeave) {
      setConfirmLeave(true);
      setTimeout(() => setConfirmLeave(false), 5000);
      return;
    }
    if (id) {
      leaveMutation.mutate(id, { onSuccess: () => router.back() });
    }
  };

  const inviteMessage = league
    ? `${t('fantasy.leaderboards.shareInvite', { name: league.name, code: league.invite_code })} https://play.google.com/store/apps/details?id=com.tashkeela.app`
    : '';

  return (
    <Screen>
      <BackHeader
        title={league?.name ?? t('fantasy.leaderboards.miniLeagues')}
        right={
          league ? <ShareButton message={inviteMessage} accessibilityLabel={t('common.share')} /> : undefined
        }
      />
      <ScrollView contentContainerStyle={{ paddingBottom: 48 }}>
        {league ? (
          <Card className="mx-4 mb-4 gap-2 px-4 py-4">
            <AppText variant="micro" weight="medium">
              {t('fantasy.leaderboards.inviteCode')}
            </AppText>
            <Pressable accessibilityRole="button" onPress={copyCode}>
              <AppText variant="heading" weight="bold" tabular className="text-volt">
                {league.invite_code}
              </AppText>
            </Pressable>
            {copied ? (
              <AppText variant="micro" className="text-live">
                {t('fantasy.leaderboards.codeCopied')}
              </AppText>
            ) : null}
          </Card>
        ) : null}

        {boardQuery.isPending ? (
          <View className="gap-3 px-4">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-14 w-full" />
            ))}
          </View>
        ) : boardQuery.isError ? (
          <ErrorState error={boardQuery.error} onRetry={() => boardQuery.refetch()} />
        ) : (boardQuery.data ?? []).length === 0 ? (
          <EmptyState
            icon="people-outline"
            title={t('fantasy.leaderboards.noLeaguesTitle')}
            body={t('fantasy.leaderboards.noLeaguesBody')}
          />
        ) : (
          <Card className="mx-4 overflow-hidden">
            {(boardQuery.data ?? []).map((entry, index) => (
              <Pressable
                key={entry.fantasy_team_id}
                accessibilityRole="button"
                accessibilityLabel={t('fantasy.leaderboards.viewSquad', { team: entry.team_name })}
                onPress={() => setSelectedEntry(entry)}
                className={`flex-row items-center gap-3 px-4 py-3 active:bg-surface-2/60 ${
                  index > 0 ? 'border-t border-line' : ''
                }`}
              >
                <AppText
                  variant="caption"
                  weight="bold"
                  tabular
                  className="w-8 text-center text-muted"
                >
                  {formatNumber(entry.rank, numeralOptions)}
                </AppText>
                <View className="flex-1">
                  <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
                    {entry.team_name}
                  </AppText>
                  <AppText variant="micro" numberOfLines={1}>
                    {entry.display_name}
                  </AppText>
                </View>
                <AppText variant="body" weight="bold" tabular className="text-volt">
                  {formatNumber(entry.total_points, numeralOptions)}
                </AppText>
              </Pressable>
            ))}
          </Card>
        )}

        <View className="mx-4 mt-6">
          <Button
            label={confirmLeave ? t('fantasy.leaderboards.leaveConfirm') : t('fantasy.leaderboards.leave')}
            variant="danger"
            compact
            loading={leaveMutation.isPending}
            onPress={onLeave}
          />
        </View>
      </ScrollView>

      <SquadViewModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
    </Screen>
  );
}
